import React from 'react'
import { Button, Input, Label, Card, CardHeader, CardContent } from '@/shared/ui'
import { CountrySelect } from '@/shared/ui/CountrySelect'
import { COUNTRY_BY_CODE, findCountryByDialPrefix } from '@/shared/data/countries'
import { useToast } from '@/shared/ui/Toast'
import { supabase } from '@/lib/supabase'
import { useAuth as useAuthContext } from '@/modules/auth/context/AuthContext'
import { useProfileForm } from '../hooks/useProfileForm'
import { uploadPublicFileWithProgress, uploadAvatarWithProgress } from '@/modules/upload/services/uploadService'
import { updateAvatarUrl } from '../services/profileService'
import { formatPhoneBR } from '../utils/phone'

export default function ProfilePage() {
  const { user, profile, loading, saving, saveBasic, setProfile } = useProfileForm() as any
  const { reloadProfile } = useAuthContext()
  const { add: toast } = useToast()

  const [fullName, setFullName] = React.useState(profile?.full_name ?? '')
  const [phone, setPhone] = React.useState('') // display formatted
  const [country, setCountry] = React.useState<string>('BR')
  const [currentPassword, setCurrentPassword] = React.useState('')
  const [newPassword, setNewPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [pwdError, setPwdError] = React.useState<string | null>(null)
  const [savingPwd, setSavingPwd] = React.useState(false)

  const fileRef = React.useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = React.useState(false)
  const [progress, setProgress] = React.useState(0)

  React.useEffect(() => {
    setFullName(profile?.full_name ?? '')
    // Deriva país e máscara a partir do phone salvo
    const raw = (profile?.phone || '').toString().trim()
    let code = (profile?.country || '').toUpperCase()
    if (!code) {
      const c = findCountryByDialPrefix(raw)
      if (c) code = c.code
    }
    if (!COUNTRY_BY_CODE.get(code)) code = 'BR'
    setCountry(code)
    // Apresentação: BR com máscara nacional; outros: agrupamento leve
    const digits = (raw.startsWith('+') ? raw.replace(/\D/g, '').replace(/^\d+/, (d: string)=>d.slice(COUNTRY_BY_CODE.get(code)!.dial.length)) : raw)
    if (code === 'BR') setPhone(formatPhoneBR(digits))
    else setPhone(digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim())
  }, [profile?.full_name, profile?.phone, profile?.country])

  async function onPickAvatar(f?: File) {
    try {
      if (!user?.id || !f) return
      setUploading(true)
      setProgress(0)
      const { url } = await uploadAvatarWithProgress(
        f,
        user.id,
        (pct) => setProgress(pct)
      )
      const newProf = await updateAvatarUrl(user.id, url)
      // Atualiza imediatamente a UI com o novo avatar
      if (newProf?.avatar_url || url) {
        setProfile((prev: any) => ({ ...(prev || {}), ...(newProf || {}), avatar_url: newProf?.avatar_url || url }))
      }
      try { await reloadProfile() } catch {}
      toast({ title: 'Avatar', description: 'Atualizado com sucesso', variant: 'success' })
    } catch (e:any) {
      toast({ title: 'Avatar', description: e?.message || 'Falha ao enviar avatar', variant: 'error' })
    } finally {
      setUploading(false)
    }
  }

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault()
    if (!user) return
    // Converte display para E.164 com base no país
    const natDigits = (phone || '').replace(/\D/g, '')
    const c = COUNTRY_BY_CODE.get(country) || COUNTRY_BY_CODE.get('BR')!
    const e164 = natDigits ? `+${c.dial}${natDigits}` : ''
    if (e164) {
      try {
        const { data } = await supabase.functions.invoke('check-phone-availability', {
          body: { phone: e164, user_id: user.id },
        })
        const resp = Array.isArray(data) ? (data as any)[0] : (data as any)
        if (resp?.available === false) {
          toast({
            title: 'Telefone',
            description: 'Este numero de telefone ja esta vinculado a outra conta. Use um numero diferente.',
            variant: 'error',
          })
          return
        }
      } catch {}
    }
    const ok = await saveBasic({
      full_name: (fullName || '').trim(),
      phone: e164 || null,
      country: country || null,
    })
    if (ok) {
      try { await reloadProfile() } catch {}
      toast({ title: 'Perfil salvo', description: 'Suas informacoes foram atualizadas.' })
    }
  }

  function validatePassword(pwd: string) {
    const re = /^(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{6,}$/
    return re.test(pwd)
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault()
    setPwdError(null)
    if (!currentPassword) { setPwdError('Informe a senha atual.'); return }
    if (!newPassword) { setPwdError('Informe a nova senha.'); return }
    if (newPassword !== confirmPassword) { setPwdError('As senhas nao coincidem.'); return }
    if (!validatePassword(newPassword)) { setPwdError('A senha deve ter no minimo 6 caracteres, 1 letra maiuscula e 1 caractere especial.'); return }
    setSavingPwd(true)
    try {
      if (!user?.email) { setPwdError('Usuario invalido.'); return }
      const email = user.email
      const { error: verifyErr } = await supabase.auth.signInWithPassword({ email, password: currentPassword })
      if (verifyErr) throw new Error('Senha atual invalida')

      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      const { error: profErr } = await supabase.from('profiles').update({ must_change_password: false }).eq('id', user.id!)
      if (profErr) console.warn('Falha ao limpar must_change_password:', profErr)
      try { setProfile((prev: any) => ({ ...(prev || {}), must_change_password: false })) } catch {}
      try { await reloadProfile() } catch {}
      setNewPassword('')
      setCurrentPassword('')
      setConfirmPassword('')
      toast({ title: 'Senha atualizada', description: 'Sua senha foi alterada com sucesso.' })
    } catch (err) {
      setPwdError((err as any)?.message ?? 'Erro ao atualizar senha')
    } finally {
      setSavingPwd(false)
    }
  }

  if (!user) return null

  return (
    <div className="mx-auto max-w-[920px] p-6">
      <h1 className="mb-6 text-2xl font-semibold">Perfil do usuario</h1>

      {profile?.must_change_password ? (
        <div className="mb-6 rounded-md border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm">
          <div className="font-medium">Atualizacao de senha obrigatoria</div>
          <div className="text-foreground/80">
            Para continuar acessando o sistema, atualize sua senha na secao &quot;Atualizar senha&quot; abaixo.
          </div>
        </div>
      ) : null}

      <Card className="mb-6">
        <CardHeader className="text-sm font-medium">Informacoes basicas</CardHeader>
        <CardContent>
          <form className="grid items-stretch gap-6 md:grid-cols-[220px,1fr]" onSubmit={saveProfile} aria-busy={savingPwd || loading}>
            <div className="flex flex-col items-center md:items-start">
              <button type="button" onClick={() => fileRef.current?.click()} className="relative h-[200px] w-[200px] overflow-hidden rounded-2xl border border-border bg-muted focus:outline-none focus:ring-2 focus:ring-primary/60">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile?.full_name || user.email || 'avatar'} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-3xl font-medium">{(profile?.full_name || user.email || '?').slice(0, 2).toUpperCase()}</div>
                )}
                <span className="absolute bottom-2 right-2 rounded-full bg-background p-2 text-foreground shadow">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                </span>
                {uploading && (
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-foreground/20">
                    <div className="h-full bg-primary" style={{ width: `${progress}%` }} />
                  </div>
                )}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPickAvatar(e.currentTarget.files?.[0] || undefined)} />
            </div>
            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-12">
                <div className="space-y-1.5 md:col-span-6">
                  <Label htmlFor="name">Nome</Label>
                  <Input id="name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Seu nome" />
                </div>
                <div className="space-y-1.5 md:col-span-6">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={user.email ?? ''} disabled />
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-12">
                <div className="space-y-1.5 md:col-span-6">
                  <Label htmlFor="country">País</Label>
                  <CountrySelect value={country} onChange={(code)=> setCountry(code)} includeDial className="h-12" />
                </div>
                <div className="space-y-1.5 md:col-span-6">
                  <Label htmlFor="phone">Celular (WhatsApp)</Label>
                  <Input id="phone" value={phone} onChange={(e) => {
                    const v = e.target.value
                    if (country === 'BR') setPhone(formatPhoneBR(v))
                    else setPhone(v.replace(/\D/g, '').replace(/(\d{3})(?=\d)/g, '$1 ').trim())
                  }} placeholder={country==='BR' ? '(11) 98765-4321' : 'número'} />
                </div>
              </div>
              <Button type="submit" disabled={saving || loading} className="min-w-28">Salvar</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="text-sm font-medium">Atualizar senha</CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={changePassword}>
            {pwdError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{pwdError}</div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="pwd-current">Senha atual</Label>
              <Input id="pwd-current" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pwd">Nova senha</Label>
              <Input id="pwd" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pwd-confirm">Confirmar nova senha</Label>
              <Input id="pwd-confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
            <Button type="submit" disabled={savingPwd} className="min-w-28">Alterar senha</Button>
          </form>
        </CardContent>
      </Card>

      <div className="mt-6" />
    </div>
  )
}
